import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { Accessibility, X, Minus, Plus, Contrast, Moon, Sun, BookOpen, MousePointer2, Zap, Underline, AlignJustify, Volume2, Square, RotateCcw } from "lucide-react";

const STORAGE_KEY = "hv-a11y";
const MIN_SCALE = 90;
const MAX_SCALE = 150;
const STEP = 10;

const DEFAULTS = {
  scale: 100,
  contrast: false,
  dyslexia: false,
  bigCursor: false,
  reduceMotion: false,
  underline: false,
  readingGuide: false,
};

const load = () => {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") };
  } catch {
    return { ...DEFAULTS };
  }
};

// Reading-guide overlay that tracks the pointer — created lazily, removed when off.
const useReadingGuide = (on) => {
  useEffect(() => {
    if (!on) return;
    const el = document.createElement("div");
    el.id = "a11y-reading-guide";
    el.style.top = "40%";
    document.body.appendChild(el);
    const move = (e) => {
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - el.offsetHeight / 2;
      el.style.top = `${Math.max(0, y)}px`;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      el.remove();
    };
  }, [on]);
};

const Row = ({ icon: Icon, label, active, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    aria-pressed={active}
    onClick={onClick}
    className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${
      active ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card hover:bg-accent text-foreground"
    }`}
  >
    <span className={`grid place-items-center h-8 w-8 rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
      <Icon className="h-4 w-4" />
    </span>
    <span className="flex-1 font-medium">{label}</span>
    <span className={`text-[10px] uppercase tracking-widest font-mono ${active ? "text-primary" : "text-muted-foreground"}`}>{active ? "On" : "Off"}</span>
  </button>
);

export const AccessibilityViewer = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = (e) => e.detail === "a11y" && setOpen(true);
    window.addEventListener("hv:open", onOpen);
    return () => window.removeEventListener("hv:open", onOpen);
  }, []);
  const [s, setS] = useState(load);
  const [speaking, setSpeaking] = useState(false);
  const { theme, setTheme } = useTheme();
  const panelRef = useRef(null);

  useReadingGuide(s.readingGuide);

  // Apply visual settings to <html> and persist.
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = s.scale === 100 ? "" : `${s.scale}%`;
    root.classList.toggle("a11y-contrast", s.contrast);
    root.classList.toggle("a11y-dyslexia", s.dyslexia);
    root.classList.toggle("a11y-bigcursor", s.bigCursor);
    root.classList.toggle("a11y-reduce-motion", s.reduceMotion);
    root.classList.toggle("a11y-underline", s.underline);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
  }, [s]);

  // Keyboard shortcut: Alt+A toggles the panel.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && (e.key === "a" || e.key === "A")) { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Stop any speech when the component unmounts.
  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  const toggle = (key) => setS((p) => ({ ...p, [key]: !p[key] }));
  const setScale = (dir) => setS((p) => ({ ...p, scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, p.scale + dir * STEP)) }));

  const reset = () => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
    setTheme("light");
    setS({ ...DEFAULTS });
  };

  const readAloud = useCallback(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (synth.speaking || speaking) { synth.cancel(); setSpeaking(false); return; }
    const main = document.querySelector("main") || document.querySelector("#root") || document.body;
    const text = (main.innerText || "").replace(/\s+/g, " ").trim().slice(0, 9000);
    if (!text) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.98;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    synth.cancel();
    synth.speak(u);
    setSpeaking(true);
  }, [speaking]);

  const darkOn = theme === "dark";

  return (
    <>
      <button
        type="button"
        data-testid="a11y-launcher"
        style={{ display: "none" }}
        aria-label="Open accessibility options"
        aria-expanded={open}
        title="Accessibility (Alt+A)"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-3 left-3 sm:bottom-5 sm:left-5 z-[2147482000] h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/20 grid place-items-center ring-2 ring-background hover:scale-105 active:scale-95 transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[2147482500] bg-black/20" onClick={() => setOpen(false)} data-testid="a11y-overlay" aria-hidden="true" />
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Accessibility options"
            data-testid="a11y-panel"
            className="fixed bottom-5 left-5 z-[2147482600] w-[min(92vw,22rem)] max-h-[85vh] overflow-y-auto rounded-2xl border border-border bg-popover text-popover-foreground shadow-2xl fade-up"
          >
            <div className="sticky top-0 flex items-center gap-2 border-b border-border bg-popover/95 backdrop-blur px-4 py-3">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-primary text-primary-foreground"><Accessibility className="h-4 w-4" /></span>
              <div className="flex-1">
                <div className="font-serif text-base leading-tight">Accessibility</div>
                <div className="text-[11px] text-muted-foreground">Saved on this device only — zero tracking.</div>
              </div>
              <button type="button" data-testid="a11y-close" aria-label="Close" onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-accent text-muted-foreground"><X className="h-4 w-4" /></button>
            </div>

            <div className="p-4 space-y-2.5">
              {/* Text size stepper */}
              <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
                <span className="grid place-items-center h-8 w-8 rounded-lg bg-muted text-muted-foreground font-serif text-sm">A</span>
                <span className="flex-1 text-sm font-medium">Text size</span>
                <div className="flex items-center gap-1">
                  <button type="button" data-testid="a11y-text-smaller" aria-label="Decrease text size" disabled={s.scale <= MIN_SCALE} onClick={() => setScale(-1)} className="h-8 w-8 grid place-items-center rounded-lg border border-border hover:bg-accent disabled:opacity-40"><Minus className="h-4 w-4" /></button>
                  <span data-testid="a11y-text-value" className="w-11 text-center text-xs font-mono text-muted-foreground">{s.scale}%</span>
                  <button type="button" data-testid="a11y-text-larger" aria-label="Increase text size" disabled={s.scale >= MAX_SCALE} onClick={() => setScale(1)} className="h-8 w-8 grid place-items-center rounded-lg border border-border hover:bg-accent disabled:opacity-40"><Plus className="h-4 w-4" /></button>
                </div>
              </div>

              <Row icon={Contrast} label="High contrast" active={s.contrast} onClick={() => toggle("contrast")} testid="a11y-contrast-toggle" />
              <Row icon={darkOn ? Sun : Moon} label="Dark mode" active={darkOn} onClick={() => setTheme(darkOn ? "light" : "dark")} testid="a11y-dark-toggle" />
              <Row icon={BookOpen} label="Dyslexia-friendly text" active={s.dyslexia} onClick={() => toggle("dyslexia")} testid="a11y-dyslexia-toggle" />
              <Row icon={MousePointer2} label="Bigger cursor" active={s.bigCursor} onClick={() => toggle("bigCursor")} testid="a11y-cursor-toggle" />
              <Row icon={Zap} label="Reduce motion" active={s.reduceMotion} onClick={() => toggle("reduceMotion")} testid="a11y-motion-toggle" />
              <Row icon={Underline} label="Underline links" active={s.underline} onClick={() => toggle("underline")} testid="a11y-underline-toggle" />
              <Row icon={AlignJustify} label="Reading guide" active={s.readingGuide} onClick={() => toggle("readingGuide")} testid="a11y-guide-toggle" />
              <Row icon={speaking ? Square : Volume2} label={speaking ? "Stop reading" : "Read page aloud"} active={speaking} onClick={readAloud} testid="a11y-read-toggle" />

              <button type="button" data-testid="a11y-reset" onClick={reset} className="w-full mt-1 flex items-center justify-center gap-2 rounded-xl border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                <RotateCcw className="h-4 w-4" /> Reset all
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityViewer;
